import { useEffect, useState } from 'react'
import { bridge } from '../../bridge'
import { useAppStore } from '../../store/app'
import { SectionHeader, Group, Row, TappableRow } from './AccountComponents'
import './AccountStyles.css'

// The web bundle's own commit, injected by vite.config.ts. Read directly
// rather than through app.info because inside a native app, app.info reports
// the NATIVE side's commit — and the two halves ship on different timelines
// (the bundle deploys with the relay; the shell goes through App Store /
// Play review). Showing one and calling it "the version" is how you end up
// testing a month-old shell against today's server and not knowing it.
const WEB_GIT_SHA: string =
  ((import.meta.env as Record<string, string | undefined>)?.VITE_GIT_SHA) || 'dev'

export function DeveloperSheet() {
  const pushSheet = useAppStore((s) => s.pushSheet)
  const showSystemMessages = useAppStore((s) => s.showSystemMessages)
  const setShowSystemMessages = useAppStore((s) => s.setShowSystemMessages)
  const [native, setNative] = useState<{ version: string; sha: string } | null>(null)

  useEffect(() => {
    if (!bridge.isNative) return
    bridge
      .call('app.info')
      .then((info) =>
        setNative({
          version: `${info.version} (${info.build})`,
          // Empty string means a build made before the stamping phase
          // existed. Say so plainly — a blank would read as "no commit",
          // which is a different and less actionable claim.
          sha: info.gitSHA || 'unknown (pre-stamping build)',
        }),
      )
      .catch(() => setNative(null))
  }, [])

  return (
    <div className="about">
      <SectionHeader>Build</SectionHeader>
      <Group>
        {native && (
          <>
            <Row label="Native app version">
              <span className="about__detail">{native.version}</span>
            </Row>
            <Row label="Native app commit">
              <span className="about__detail">{native.sha}</span>
            </Row>
          </>
        )}
        <Row label="Web commit">
          <span className="about__detail">{WEB_GIT_SHA}</span>
        </Row>
      </Group>

      <SectionHeader>Transcript</SectionHeader>
      <Group>
        <div className="about__row">
          <span className="about__row-label">Show system messages</span>
          <label className="about__toggle">
            <input
              type="checkbox"
              checked={showSystemMessages}
              onChange={(e) => setShowSystemMessages(e.target.checked)}
            />
            <span className="about__toggle-track" />
          </label>
        </div>
      </Group>

      <SectionHeader>Entity CRUD</SectionHeader>
      <Group>
        <TappableRow label="Hosts" onClick={() => pushSheet({ kind: 'hosts_debug' })}>
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow label="Devices" onClick={() => pushSheet({ kind: 'devices_debug' })}>
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow label="Positions" onClick={() => pushSheet({ kind: 'positions_debug' })}>
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow
          label="Working Directories"
          onClick={() => pushSheet({ kind: 'working_directories_debug' })}
        >
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow
          label="Resource Connections"
          onClick={() => pushSheet({ kind: 'resource_connections_debug' })}
        >
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow
          label="Members"
          onClick={() => pushSheet({ kind: 'organization_members_debug' })}
        >
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow
          label="Job Descriptions"
          onClick={() => pushSheet({ kind: 'agent_job_descriptions_debug' })}
        >
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow label="Secrets" onClick={() => pushSheet({ kind: 'secrets_debug' })}>
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow
          label="Installed Plugins"
          onClick={() => pushSheet({ kind: 'plugin_installs_debug' })}
        >
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow label="Rules" onClick={() => pushSheet({ kind: 'rules_debug' })}>
          <span className="about__chevron">›</span>
        </TappableRow>
        <TappableRow label="Approvers" onClick={() => pushSheet({ kind: 'approvers' })}>
          <span className="about__chevron">›</span>
        </TappableRow>
      </Group>
    </div>
  )
}
